/**
 * Maslul sticker deployment — the GROUND TRUTH for `MaslulAskLog.campaign`.
 *
 * Every printed QR sticker encodes `utm_campaign = <park>-<utm>` (see
 * tools/maslul-stickers/stamp_qr.py `url_for`). This table maps every DEPLOYED
 * (park, utm) to its sticker design + Hebrew headline, derived from the field-survey
 * photos (docs/maslul/sticker-deployment.md, sourced from docs/maslul/images/all.pdf).
 *
 * Used by the admin campaign analytics to attribute each scan to a park + design and to
 * flag any campaign NOT in this table as "unexpected", and by the relabel migration
 * (packages/db/scripts/relabel-campaigns.ts) to conform the live event log to it.
 */
export const MASLUL_STICKER_DEPLOYMENT = [
    // ---- g1200 (/p/g1200) — by elimination: the designs NOT seen in the galit/glaguardia photos ----
    { park: 'g1200', utm: 'free', design: 'free', headline: 'חינם. · מאמן כושר אישי לפארק הזה', confident: true },
    { park: 'g1200', utm: 'community', design: 'community', headline: 'קהילה בריאה מתאמנת כאן', confident: true },
    { park: 'g1200', utm: 'allages', design: 'all-ages', headline: 'מתאים לכל גיל, בבטחה', confident: true },
    {
        park: 'g1200',
        utm: 'howto',
        design: 'how-to',
        headline: 'ככה זה עובד (סרקו · בחרו מתקן · צאו לדרך)',
        confident: true,
    },
    { park: 'g1200', utm: 'bold', design: 'bold', headline: 'סרקו. התאמנו. זהו.', confident: true },
    { park: 'g1200', utm: 'b', design: 'discover', headline: 'לא יודעים איך להתאמן כאן? סרקו וגלו', confident: false },
    // g1200 carries its own A/B set that overlaps galit's — confirmed by live scans
    // (g1200-ask). Add observed g1200 campaigns here as the data surfaces them.
    { park: 'g1200', utm: 'ask', design: 'ask', headline: 'שאלו את הפארק כל שאלה', confident: true },
    // ---- galit (/p/galit) — confirmed in the survey photos ----
    { park: 'galit', utm: 'nogym', design: 'no-gym', headline: 'כל מה שחדר כושר נותן. בפארק. בחינם.', confident: true },
    { park: 'galit', utm: 'ask', design: 'ask', headline: 'שאלו את הפארק כל שאלה', confident: true },
    {
        park: 'galit',
        utm: 'a',
        design: 'coach-illustrated',
        headline: 'מאמן כושר אישי לפארק הזה (איור)',
        confident: false,
    },
    {
        park: 'galit',
        utm: 'g',
        design: 'coach-photo',
        headline: 'מאמן כושר אישי לפארק הזה — חינם (תצלום)',
        confident: false,
    },
    { park: 'galit', utm: 'decal-generic', design: 'wayfinding', headline: 'לא יודעים איך? סרקו', confident: true },
    // ---- glaguardia (/p/glaguardia) — confirmed in the survey photo ----
    {
        park: 'glaguardia',
        utm: 'decal-pullup',
        design: 'pullup-decal',
        headline: 'ככה עושים. סרקו להדרכה',
        confident: true,
    },
];
/** The canonical `utm_campaign` string for a deployment row. */
export function stickerCampaign(d) {
    return `${d.park}-${d.utm}`;
}
const BY_CAMPAIGN = Object.fromEntries(MASLUL_STICKER_DEPLOYMENT.map((d) => [stickerCampaign(d), d]));
/** Every campaign string the deployment table considers ground truth. */
export const CANONICAL_CAMPAIGNS = MASLUL_STICKER_DEPLOYMENT.map(stickerCampaign);
/**
 * Resolve a raw `MaslulAskLog.campaign` to its park + design per the ground-truth table.
 * Exact match → known:true. Otherwise still surfaces the park if the value parses as
 * `<park>-<utm>`, with known:false (an unexpected / non-deployed campaign).
 */
export function campaignInfo(campaign) {
    if (!campaign)
        return null;
    const hit = BY_CAMPAIGN[campaign];
    if (hit)
        return { park: hit.park, design: hit.design, headline: hit.headline, known: true, confident: hit.confident };
    const dash = campaign.indexOf('-');
    const park = dash > 0 ? campaign.slice(0, dash) : '';
    return { park, design: campaign, headline: '', known: false, confident: false };
}
export function isKnownCampaign(campaign) {
    return !!campaign && Object.prototype.hasOwnProperty.call(BY_CAMPAIGN, campaign);
}
//# sourceMappingURL=maslul-stickers.js.map